/**
 * Material Access Control Modal Component
 *
 * Allows Directors and Managers to grant extra View/Print counts
 * to Admin and Teacher users for specific materials.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { ModalOverlay, ModalContainer } from './BaseModal';
import { listUsers } from '../../api/portalUserApi';
import {
  getMaterialControls,
  setMaterialControl,
  deleteMaterialControl
} from '../../api/materialPortalApi';

// Styled components
const Modal = styled(ModalContainer)`
  max-width: 700px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    margin: 0;
    font-size: 18px;
    color: #1f2937;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 0;

  &:hover {
    color: #1f2937;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 12px;
  align-items: end;
  margin-bottom: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
  }
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  background: ${props => props.$color || '#8b5cf6'};
  color: white;
  white-space: nowrap;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ControlCard = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
`;

const ControlHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const UserInfo = styled.div`
  h5 {
    margin: 0 0 4px 0;
    font-size: 15px;
    color: #1f2937;
  }

  span {
    font-size: 12px;
    color: #6b7280;
  }
`;

const RoleBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => props.$role === 'portal_admin' ? '#dbeafe' : '#fef3c7'};
  color: ${props => props.$role === 'portal_admin' ? '#1d4ed8' : '#92400e'};
  margin-left: 8px;
`;

const ControlDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 12px;
  align-items: end;
`;

const CountDisplay = styled.div`
  font-size: 13px;
  color: #4b5563;

  strong {
    color: #1f2937;
    font-size: 18px;
    margin-right: 4px;
  }
`;

const GrantedBy = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const SmallButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${props => props.$danger ? '#ef4444' : '#d1d5db'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  background: ${props => props.$danger ? '#fef2f2' : 'white'};
  color: ${props => props.$danger ? '#dc2626' : '#4b5563'};

  &:hover:not(:disabled) {
    background: ${props => props.$danger ? '#fee2e2' : '#f3f4f6'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: #6b7280;
  font-size: 14px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 32px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const MaterialAccessControlModal = ({
  show,
  onClose,
  material
}) => {
  const [controls, setControls] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state for adding new control
  const [selectedUserId, setSelectedUserId] = useState('');
  const [extraViewCount, setExtraViewCount] = useState(0);
  const [extraPrintCount, setExtraPrintCount] = useState(0);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editViewCount, setEditViewCount] = useState(0);
  const [editPrintCount, setEditPrintCount] = useState(0);

  // Load data
  const loadData = useCallback(async () => {
    if (!material?.id) return;

    setLoading(true);
    setError(null);

    try {
      const [controlsData, usersData] = await Promise.all([
        getMaterialControls(material.id),
        listUsers()
      ]);

      setControls(controlsData);

      // Filter to only Admin and Teacher users
      const eligible = usersData.filter(u =>
        u.roles?.includes('portal_admin') || u.roles?.includes('portal_teacher')
      );
      setEligibleUsers(eligible);

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [material?.id]);

  useEffect(() => {
    if (show) {
      loadData();
    }
  }, [show, loadData]);

  // Get user role for display
  const getUserRole = (user) => {
    if (user.roles?.includes('portal_admin')) return 'portal_admin';
    if (user.roles?.includes('portal_teacher')) return 'portal_teacher';
    return null;
  };

  // Get users not already in controls
  const availableUsers = eligibleUsers.filter(
    u => !controls.some(c => c.userId === u.id)
  );

  // Handle add new control
  const handleAdd = async () => {
    if (!selectedUserId) {
      alert('Please select a user');
      return;
    }

    setSaving(true);
    try {
      const newControl = await setMaterialControl(
        material.id,
        selectedUserId,
        extraViewCount,
        extraPrintCount
      );

      setControls(prev => [...prev, newControl]);
      setSelectedUserId('');
      setExtraViewCount(0);
      setExtraPrintCount(0);

    } catch (err) {
      console.error('Error adding control:', err);
      alert(`Failed to add access: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle update control
  const handleUpdate = async (control) => {
    setSaving(true);
    try {
      const updated = await setMaterialControl(
        material.id,
        control.userId,
        editViewCount,
        editPrintCount
      );

      setControls(prev => prev.map(c =>
        c.id === control.id ? updated : c
      ));
      setEditingId(null);

    } catch (err) {
      console.error('Error updating control:', err);
      alert(`Failed to update access: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle delete control
  const handleDelete = async (controlId) => {
    if (!window.confirm('Are you sure you want to remove this extra access?')) {
      return;
    }

    setSaving(true);
    try {
      await deleteMaterialControl(controlId);
      setControls(prev => prev.filter(c => c.id !== controlId));
    } catch (err) {
      console.error('Error deleting control:', err);
      alert(`Failed to remove access: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Start editing
  const startEdit = (control) => {
    setEditingId(control.id);
    setEditViewCount(control.extraViewCount || 0);
    setEditPrintCount(control.extraPrintCount || 0);
  };

  if (!show) return null;

  return (
    <ModalOverlay>
      <Modal>
        <ModalHeader>
          <h3>Access Control - {material?.name}</h3>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalContent>
          {error && <ErrorState>{error}</ErrorState>}

          {loading ? (
            <LoadingState>Loading...</LoadingState>
          ) : (
            <>
              {/* Add New Access Section */}
              <Section>
                <SectionTitle>Grant Extra Access</SectionTitle>
                <FormRow>
                  <FormGroup>
                    <label>User</label>
                    <Select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                    >
                      <option value="">Select a user...</option>
                      {availableUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name || user.email} ({getUserRole(user) === 'portal_admin' ? 'Admin' : 'Teacher'})
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <label>Extra View</label>
                    <Input
                      type="number"
                      min="0"
                      value={extraViewCount}
                      onChange={(e) => setExtraViewCount(parseInt(e.target.value) || 0)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Extra Print</label>
                    <Input
                      type="number"
                      min="0"
                      value={extraPrintCount}
                      onChange={(e) => setExtraPrintCount(parseInt(e.target.value) || 0)}
                    />
                  </FormGroup>
                  <Button
                    onClick={handleAdd}
                    disabled={saving || !selectedUserId}
                  >
                    Add
                  </Button>
                </FormRow>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  Default limits: Admin = unlimited View + 3 Print, Teacher = 3 View + 0 Print
                </div>
              </Section>

              {/* Current Grants Section */}
              <Section>
                <SectionTitle>Current Extra Access ({controls.length})</SectionTitle>
                {controls.length === 0 ? (
                  <EmptyState>
                    No extra access has been granted for this material.
                  </EmptyState>
                ) : (
                  controls.map(control => (
                    <ControlCard key={control.id}>
                      <ControlHeader>
                        <UserInfo>
                          <h5>
                            {control.userName || 'Unknown User'}
                            <RoleBadge $role={eligibleUsers.find(u => u.id === control.userId)?.roles?.includes('portal_admin') ? 'portal_admin' : 'portal_teacher'}>
                              {eligibleUsers.find(u => u.id === control.userId)?.roles?.includes('portal_admin') ? 'Admin' : 'Teacher'}
                            </RoleBadge>
                          </h5>
                          <span>{control.userEmail}</span>
                        </UserInfo>
                      </ControlHeader>

                      {editingId === control.id ? (
                        <ControlDetails>
                          <FormGroup>
                            <label>Extra View</label>
                            <Input
                              type="number"
                              min="0"
                              value={editViewCount}
                              onChange={(e) => setEditViewCount(parseInt(e.target.value) || 0)}
                            />
                          </FormGroup>
                          <FormGroup>
                            <label>Extra Print</label>
                            <Input
                              type="number"
                              min="0"
                              value={editPrintCount}
                              onChange={(e) => setEditPrintCount(parseInt(e.target.value) || 0)}
                            />
                          </FormGroup>
                          <div></div>
                          <ActionButtons>
                            <SmallButton onClick={() => handleUpdate(control)} disabled={saving}>
                              Save
                            </SmallButton>
                            <SmallButton onClick={() => setEditingId(null)}>
                              Cancel
                            </SmallButton>
                          </ActionButtons>
                        </ControlDetails>
                      ) : (
                        <ControlDetails>
                          <CountDisplay>
                            <strong>+{control.extraViewCount || 0}</strong> Extra View
                          </CountDisplay>
                          <CountDisplay>
                            <strong>+{control.extraPrintCount || 0}</strong> Extra Print
                          </CountDisplay>
                          <div></div>
                          <ActionButtons>
                            <SmallButton onClick={() => startEdit(control)}>
                              Edit
                            </SmallButton>
                            <SmallButton $danger onClick={() => handleDelete(control.id)} disabled={saving}>
                              Remove
                            </SmallButton>
                          </ActionButtons>
                        </ControlDetails>
                      )}

                      <GrantedBy>
                        Granted by {control.grantedByName || 'Unknown'} on {new Date(control.updatedAt || control.createdAt).toLocaleString()}
                      </GrantedBy>
                    </ControlCard>
                  ))
                )}
              </Section>
            </>
          )}
        </ModalContent>

        <ModalFooter>
          <Button $color="#6b7280" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </ModalOverlay>
  );
};

export default MaterialAccessControlModal;
